/**
 * This React provider checks that the user is logged in. It allows child
 * components to call useAdmin() and useTenant() to get information about the
 * logged-in admin and their tenant.
 */
import React, { useContext, useEffect } from "react";
import { useHistory } from "react-router-dom";

import { Role } from "src/graphql/generated/globalTypes";
import {
  loggedInInfo_loggedInInfo_subaccountMemberships_subaccount,
  loggedInInfo_loggedInInfo_subaccountMemberships
} from "src/graphql/generated/loggedInInfo";
import { setLastUsedTimeForSubaccount } from "src/util/subaccountUsage";

import {
  useAdminSubaccountMemberships,
  useTenant
} from "./LoggedInAdminProvider";
import ErrorPageLayout from "./login-errors/Layout";

export type Subaccount =
  loggedInInfo_loggedInInfo_subaccountMemberships_subaccount;
export type SubaccountMembership =
  loggedInInfo_loggedInInfo_subaccountMemberships;
export type SubaccountRole = Role;

type SubaccountContextValue = {
  membership: SubaccountMembership;
  subaccount: Subaccount;
  role: SubaccountRole;
  subaccountLink: (relativePath: string) => string;
} | null;

export const SubaccountContext =
  React.createContext<SubaccountContextValue>(null);

export function useSubaccount(): SubaccountContextValue | null {
  return useContext(SubaccountContext);
}

function ErrorPage({ error }: { error: string | JSX.Element }): JSX.Element {
  return <ErrorPageLayout title="Error">{error}</ErrorPageLayout>;
}

export default function SubaccountProvider({
  children,
  explicitSubaccountId
}: {
  children: React.ReactNode;
  explicitSubaccountId: string | null;
}): JSX.Element {
  const history = useHistory();
  const tenant = useTenant();
  const memberships = useAdminSubaccountMemberships();
  if (!memberships || !tenant) {
    throw new Error(
      "Cannot render SubaccountProvider without tenant and memberships"
    );
  }

  let membership: loggedInInfo_loggedInInfo_subaccountMemberships | undefined;
  if (explicitSubaccountId === null) {
    // no explicit subaccount ID -- use the default subaccount
    membership = memberships.find(
      membership => membership.subaccount.id === tenant.defaultSubaccount?.id
    );
  } else {
    membership = memberships.find(
      membership => membership.subaccount.id === explicitSubaccountId
    );
  }

  let redirectDestination: string | undefined;
  let routingError: string | JSX.Element | undefined;
  if (!membership) {
    // we couldn't find a matching membership. Redirect the user somewhere else.
    if (tenant.defaultSubaccount) {
      // the tenant is in non-subaccount mode, so redirect to the non-subaccount
      // routes. But first check that they have a membership in that subaccount.
      const subaccountMembership = memberships.find(
        membership => membership.subaccount.id === tenant.defaultSubaccount?.id
      );
      if (subaccountMembership) {
        redirectDestination = `/admin/campaigns`;
      } else {
        routingError =
          "You do not have access to the default subaccount for this organization. Please contact Scale to Win support.";
      }
    } else {
      // The tenant is in subaccount mode. If the user has only one membership,
      // send them there. Otherwise, send them to the subaccount list.
      if (memberships.length === 0) {
        routingError =
          "You do not have access to any subaccounts for this organization. Please contact Scale to Win support.";
      } else if (memberships.length === 1) {
        redirectDestination = `/subaccounts/${memberships[0].subaccount.id}/campaigns`;
      } else {
        redirectDestination = `/user/subaccounts`;
      }
    }
  }

  useEffect(() => {
    if (redirectDestination) {
      history.push(redirectDestination);
    }
  }, [redirectDestination, history]);

  useEffect(() => {
    if (membership?.subaccount.id) {
      setLastUsedTimeForSubaccount(membership.subaccount.id);
    }
  }, [membership?.subaccount.id]);

  if (routingError) {
    return <ErrorPage error={routingError} />;
  }

  if (!membership) {
    // we will redirect above
    return <div />;
  }

  function subaccountLink(relativePath: string): string {
    if (explicitSubaccountId) {
      return `/subaccounts/${explicitSubaccountId}${relativePath}`;
    } else {
      return `/admin${relativePath}`;
    }
  }

  return (
    <SubaccountContext.Provider
      value={{
        membership,
        subaccount: membership.subaccount,
        role: membership.role,
        subaccountLink
      }}
    >
      {children}
    </SubaccountContext.Provider>
  );
}
